import { useFeatureFlagSplit } from '@verantos-dev/app-state-base';
import {
  selectAnalyticalVariables,
  selectCriteria,
  selectStudyPeriodEndDate,
  selectStudyPeriodStartDate,
  useCohortDefinitionStore,
} from '@verantos-dev/app-state-cohort-definition';
import { CriteriaSelection } from '@verantos-dev/types-cohorts';

export interface CohortValidationResult {
  /** Has at least one resolved criterion AND an index date AV definition. */
  hasDslValue: boolean;
  /** A resolved criterion is flagged as the index date. */
  isIndexDateDefined: boolean;
  /** Study period start AND end are both set. */
  isStudyPeriodDefined: boolean;
  /** Any AV's concept group is in an unresolved/error state. */
  hasConceptGroupErrors: boolean;
  /** At least one criterion has neither an analyticalVariableId nor a sourceLibraryAvId. */
  hasUnboundCriteria: boolean;
  /** Two criteria bind the same library AV to the same inclusion/exclusion/index role (OR-6381). */
  hasDuplicateCriteria: boolean;
  /** Definition is fit for processing — gates the Process button. */
  isCohortDefinitionValid: boolean;
}

type CriterionElement = ReturnType<typeof selectCriteria>[number];

/** Returns true when an AV-backed criterion has at least one resolvable reference. */
export const isCriterionResolved = (c: CriterionElement): boolean => {
  if (c.criteriaType === 'analytical_variable') {
    return !!(c.analyticalVariableId || c.sourceLibraryAvId);
  }
  if (c.criteriaType === 'index_date') {
    if (c.value.type !== 'Analytical variable') return true;
    return !!(c.value.analyticalVariableId || c.value.sourceLibraryAvId);
  }
  return true;
};

/**
 * The (library AV id, role) a criterion is bound to, or `null` when the criterion
 * doesn't carry a library reference (unbound stub, or a local-only AV with no
 * `sourceLibraryAvId`). Mirrors the backend's `ReferencedAV` derivation
 * (`cohort_service.cohort_processing.referenced_avs.referenced_analytical_variables`)
 * exactly, so the two can never disagree about what counts as a duplicate.
 */
const criterionLibraryRef = (c: CriterionElement): { avId: string; role: string } | null => {
  if (c.criteriaType === 'analytical_variable' && c.sourceLibraryAvId) {
    // Mirrors the schema's own default (CriteriaSelectionSchema.default(INCLUSION)) —
    // a criterion built in-memory before the selection is set shouldn't crash this check.
    const selection = c.criteriaSelection ?? CriteriaSelection.INCLUSION;
    return {
      avId: c.sourceLibraryAvId,
      role: selection.toLowerCase().replace(/ /g, '_'),
    };
  }
  if (
    c.criteriaType === 'index_date' &&
    c.value.type === 'Analytical variable' &&
    c.value.sourceLibraryAvId
  ) {
    return { avId: c.value.sourceLibraryAvId, role: 'index' };
  }
  return null;
};

/**
 * True when two criteria bind the same library AV to the same inclusion/exclusion/
 * index role (OR-6381) — the shape that makes the SQL translator emit an unaliased
 * duplicate JOIN and strands cohort processing. The same AV bound to *different*
 * roles (e.g. also used as the index date) is a legitimate, un-flagged pattern.
 */
export const hasDuplicateCriteria = (criteria: CriterionElement[]): boolean => {
  const seen = new Set<string>();
  for (const c of criteria) {
    const ref = criterionLibraryRef(c);
    if (!ref) continue;
    const key = `${ref.avId}::${ref.role}`;
    if (seen.has(key)) return true;
    seen.add(key);
  }
  return false;
};

/**
 * Gen-3 cohort validation. AV-only — no event-criteria or event-name
 * checks (those belong to the legacy v0 model). Reads exclusively from
 * the Zustand cohort-definition store; no Redux, no TanStack.
 *
 * Used by `useCohortDetailActions` to gate the Process / Save buttons.
 * Validation messages (tooltips on disabled buttons) are computed in
 * the actions hook; this one only reports the booleans.
 */
export const useCohortValidation = (): CohortValidationResult => {
  const isRaGen3Enabled = useFeatureFlagSplit('research_assistant_gen_3');
  const analyticalVariables = useCohortDefinitionStore(selectAnalyticalVariables);
  const criteria = useCohortDefinitionStore(selectCriteria);
  const studyPeriodStart = useCohortDefinitionStore(selectStudyPeriodStartDate);
  const studyPeriodEnd = useCohortDefinitionStore(selectStudyPeriodEndDate);
  // Aggregate over the per-AV error map — the store keeps a count per AV;
  // any non-zero count means at least one concept group has an unresolved
  // error somewhere in the cohort.
  const hasConceptGroupErrors = useCohortDefinitionStore((state) =>
    Object.values(state.conceptGroupErrorCounts).some((count) => count > 0)
  );

  const resolvedCriteria = criteria.filter(isCriterionResolved);
  const hasUnboundCriteria = resolvedCriteria.length < criteria.length;
  const hasDuplicateAvCriteria = hasDuplicateCriteria(criteria);

  const hasDslValue =
    resolvedCriteria.length > 0 && (isRaGen3Enabled ? true : analyticalVariables.length > 0);
  const isIndexDateDefined = resolvedCriteria.some((c) => c.criteriaType === 'index_date');
  const isStudyPeriodDefined = !!(studyPeriodStart && studyPeriodEnd);

  const isCohortDefinitionValid =
    hasDslValue &&
    isIndexDateDefined &&
    isStudyPeriodDefined &&
    !hasConceptGroupErrors &&
    !hasUnboundCriteria &&
    !hasDuplicateAvCriteria;

  return {
    hasDslValue,
    isIndexDateDefined,
    isStudyPeriodDefined,
    hasConceptGroupErrors,
    hasUnboundCriteria,
    hasDuplicateCriteria: hasDuplicateAvCriteria,
    isCohortDefinitionValid,
  };
};
